import React, { useEffect, useRef, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { ListingState } from '__generated__/athena/graphql';
import { corePdpLink } from 'components/helpers/links';
import type { MarketplaceFavoriteOrigin } from 'components/marketplace/helpers/__generated__/marketplace-favorites.zeus';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import {
  type MarketplaceFavoriteChangeHandler,
  useMarketplaceFavorite,
} from 'components/marketplace/helpers/useMarketplaceFavorite';
import dynamic from 'next/dynamic';

import { CardLink, PropertyCard } from '../PropertyCard';
import { streetViewLocationFor, StreetViewPhoto } from '../shared/StreetViewPhoto';
import { buildAddressLines, buildPropertyPhotoUrl, convertDwellingType } from './helpers';
import type { ListingCardData } from './types';

// SSR must render the same photo tree that hydrates; viewport checks belong on its controls.
const PropertyCardGallery = dynamic(() =>
  import('./PropertyCardGallery').then((module) => module.PropertyCardGallery),
);

const EMPTY_PHOTO_URLS: ReadonlyArray<string> = [];

export type PhotoLoadMode = 'deferred' | 'priority';

type UseListingPhotoSourceOptions = {
  hasPhoto: boolean;
  loadMode: PhotoLoadMode;
};

const useListingPhotoSource = ({ hasPhoto, loadMode }: UseListingPhotoSourceOptions) => {
  const [shouldLoadPhoto, setShouldLoadPhoto] = useState(loadMode === 'priority');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || shouldLoadPhoto) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadPhoto(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [shouldLoadPhoto]);

  return {
    containerRef,
    isPhotoPending: !shouldLoadPhoto && hasPhoto,
    shouldLoadPhoto,
  };
};

interface Tracking {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
}

interface SimilarListingsProps {
  listings: ListingCardData[];
  title?: string | null;
}

interface ListingCardComponentProps {
  listing: ListingCardData;
  photoLoadMode?: PhotoLoadMode;
  /** Desktop search cards opt into an interactive photo gallery; other surfaces stay static. */
  photoMode?: 'gallery' | 'static';
  favoriteOrigin?: MarketplaceFavoriteOrigin;
  initialFavoritePending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
}

export const ListingCard: React.FC<ListingCardComponentProps & Tracking> = ({
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
  listing,
  photoLoadMode = 'deferred',
  photoMode = 'static',
  favoriteOrigin = 'LISTING_DETAILS',
  initialFavoritePending,
  onFavoriteChange,
}) => {
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: listing.addressToken,
    origin: favoriteOrigin,
    initialIsLiked: listing.isLiked,
    initialFavoritePending,
    onFavoriteChange,
    priceCents: listing.listPrice == null ? null : dollarsToCents(listing.listPrice),
  });

  const { containerRef, isPhotoPending, shouldLoadPhoto } = useListingPhotoSource({
    hasPhoto: listing.photos.length > 0,
    loadMode: photoLoadMode,
  });
  const photoUrl = buildPropertyPhotoUrl(listing.photos[0], {
    preset: '3:2-md',
    presetScale: '2/3',
    dpr: '1',
  });

  const query = new URLSearchParams({
    ...(utmSource && { utm_source: utmSource }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmCampaign && { utm_campaign: utmCampaign }),
    ...(utmContent && { utm_content: utmContent }),
    ...(utmTerm && { utm_term: utmTerm }),
  }).toString();
  const propertyLink = corePdpLink(listing.slug ?? '_', listing.addressToken);
  const link = `${propertyLink}${query ? `?${query}` : ''}`;

  const { primaryLine, secondaryLine } = buildAddressLines(listing);

  // Photography gates going live, so available-soon homes are photo-less by
  // construction — street view stands in when we have the geo point.
  const isAvailableSoon = listing.listingState === ListingState.AVAILABLE_SOON;
  const streetViewLocation = streetViewLocationFor({
    availableSoon: isAvailableSoon,
    photoCount: listing.photos.length,
    latitude: listing.latitude,
    longitude: listing.longitude,
  });

  let photoMedia: React.ReactElement | undefined;
  if (streetViewLocation) {
    photoMedia = (
      <CardLink
        href={link}
        ariaLabel="Main view of property"
        analyticsName="marketplace-property-card-image-link"
        className="h-full w-full"
      >
        <StreetViewPhoto
          latitude={streetViewLocation.latitude}
          longitude={streetViewLocation.longitude}
          alt={`Street view of ${listing.fullAddress ?? 'this home'}`}
          className="aspect-[16/9] w-full"
          fallbackLabel="Photos coming soon"
        />
      </CardLink>
    );
  } else if (photoMode === 'gallery') {
    photoMedia = (
      <PropertyCardGallery
        key={listing.addressToken}
        addressId={listing.addressToken}
        href={link}
        isPhotoPending={isPhotoPending}
        placeholderLabel={isAvailableSoon ? 'Photos coming soon' : undefined}
        previewPhotoUrls={shouldLoadPhoto ? listing.photos : EMPTY_PHOTO_URLS}
        fetchPriority={photoLoadMode === 'priority' ? 'high' : undefined}
      />
    );
  }

  return (
    <Col ref={containerRef} className="w-full">
      <PropertyCard
        baths={listing.baths}
        beds={listing.beds}
        city={secondaryLine}
        dwellingType={convertDwellingType(listing.dwellingType)}
        href={link}
        isFavorite={isLiked}
        isFavoriteLoading={isFavoriteLoading}
        neighborhood={primaryLine}
        onFavoritePress={onToggleFavorite}
        isPhotoPending={isPhotoPending}
        photoMedia={photoMedia}
        photoUrl={shouldLoadPhoto ? photoUrl : null}
        photoFetchPriority={photoLoadMode === 'priority' ? 'high' : undefined}
        price={listing.listPrice}
        selfTourAvailable={listing.canSelfTour}
        statusLabel={isAvailableSoon ? 'Available soon' : null}
        sqft={listing.livingSquareFootage}
      />
    </Col>
  );
};

const DISPLAYED_LISTING_SET_COUNT = 6;

export const SimilarListings: React.FC<SimilarListingsProps & Tracking> = ({
  listings,
  title,
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
}) => {
  const [displayedListingCount, setDisplayedListingCount] = React.useState(
    DISPLAYED_LISTING_SET_COUNT,
  );

  const incrementDisplayedListingCount = () => {
    setDisplayedListingCount((prev) => prev + DISPLAYED_LISTING_SET_COUNT);
  };

  const similarListings = listings.slice(0, displayedListingCount).map((listing) => (
    <Col key={listing.addressToken} className="w-full pb-6 lg:w-[48%]">
      <ListingCard
        listing={listing}
        utmSource={utmSource}
        utmMedium={utmMedium}
        utmCampaign={utmCampaign}
        utmContent={utmContent}
        utmTerm={utmTerm}
      />
    </Col>
  ));

  return (
    <Col className="pb-31 md:pb-6">
      <Text as="h5" className="pb-6 text-subheader-xs text-od-content-primary">
        {title || 'Similar listings'}
      </Text>
      <Col className="flex-wrap justify-between lg:flex-row">{similarListings}</Col>
      {displayedListingCount < listings.length && (
        <Button
          onClick={incrementDisplayedListingCount}
          variant="secondary"
          size="lg"
          analyticsName="marketplace-show-more-listings"
          aria-label="See more homes"
          className="w-full"
        >
          See more homes
        </Button>
      )}
    </Col>
  );
};
